import {inject, injectable} from 'inversify';
import {Observable} from 'rxjs';
import type {
    AdminBulkGrantRequest,
    AdminGrantCreditsRequest,
    AdminGrantEntitlementRequest,
    AdminRevokeEntitlementRequest,
} from 'xconvert-web-shared';

import {ResponsePipe} from '../utils/request.utils';
import {ApiConfig} from './api-config';
import {LightHttp} from './light-http.service';
import {LoginService} from './login.service';

/**
 * Admin-side billing operations service per payments-redesign.md §F10
 * (Stream D / D7). Wraps the `/admin/users/{userId}/billing/*` and
 * `/admin/billing/*` endpoints shipped in Stream B (B5/B6/B7).
 *
 * Used by the admin portal pages — NOT by user-facing portal code.
 * The admin endpoints require an admin JWT (validated server-side via
 * the user.admin flag); calls from non-admin sessions return 403.
 *
 * Same shape as [BillingService] but for the admin surface — separate
 * class so dependency-injection wiring keeps user/admin concerns
 * isolated.
 *
 * **Response shapes are loose** (`Record<string, unknown>`) for the
 * admin endpoints because the server returns flexible per-action
 * shapes (overview includes recent events + grants nested; grant
 * actions return ids); the admin portal renders them with manual
 * narrowing rather than pinning to typed DTOs. A typed DTO surface
 * lands in a follow-up if admin-portal complexity grows.
 */
@injectable()
export class AdminBillingService {
    constructor(
        @inject(ApiConfig) private apiConfig: ApiConfig,
        @inject(LightHttp) private http: LightHttp,
        @inject(LoginService) private loginService: LoginService,
    ) {}

    /** GET /admin/users/{userId}/billing — admin overview. */
    overview(userId: string): Observable<Record<string, unknown>> {
        this.requireLogin();
        return this.http
            .get<Record<string, unknown>>(
                this.apiConfig.getAdminUserBillingOverviewEndpoint(userId),
            )
            .pipe(ResponsePipe);
    }

    grantEntitlement(
        userId: string,
        body: AdminGrantEntitlementRequest,
    ): Observable<Record<string, unknown>> {
        this.requireLogin();
        return this.http
            .post<Record<string, unknown>>(
                this.apiConfig.getAdminUserBillingGrantEntitlementEndpoint(userId),
                body,
            )
            .pipe(ResponsePipe);
    }

    revokeEntitlement(
        userId: string,
        body: AdminRevokeEntitlementRequest,
    ): Observable<Record<string, unknown>> {
        this.requireLogin();
        return this.http
            .post<Record<string, unknown>>(
                this.apiConfig.getAdminUserBillingRevokeEntitlementEndpoint(userId),
                body,
            )
            .pipe(ResponsePipe);
    }

    grantCredits(
        userId: string,
        body: AdminGrantCreditsRequest,
    ): Observable<Record<string, unknown>> {
        this.requireLogin();
        return this.http
            .post<Record<string, unknown>>(
                this.apiConfig.getAdminUserBillingGrantCreditsEndpoint(userId),
                body,
            )
            .pipe(ResponsePipe);
    }

    grantBulk(body: AdminBulkGrantRequest): Observable<Record<string, unknown>> {
        this.requireLogin();
        return this.http
            .post<Record<string, unknown>>(
                this.apiConfig.getAdminBillingGrantBulkEndpoint(),
                body,
            )
            .pipe(ResponsePipe);
    }

    validate(userId: string): Observable<Record<string, unknown>> {
        this.requireLogin();
        return this.http
            .post<Record<string, unknown>>(
                this.apiConfig.getAdminBillingValidateEndpoint(userId),
                {},
            )
            .pipe(ResponsePipe);
    }

    private requireLogin(): void {
        if (!this.loginService.isUserLoggedIn) {
            throw new Error(
                'Admin login required. Sign in with an admin account to access admin billing endpoints.',
            );
        }
    }
}
